/**
 * Dialogue Domain
 * Types, Zustand store, and loader for the dialogue system.
 * Dialogue definitions are JSON files in src/data/dialogues/.
 */

import { handleDialogueClosed } from './team-activity';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { sendToMatch } from '../services/nakama/match-send';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DialogueLine {
  characterId: string;
  textKey: string;
  textParams?: Record<string, string>;
  /** False when text was revised and its cached narration has not yet been regenerated. */
  audioEnabled?: boolean;
}

export interface DialogueConfig {
  autoAdvance: boolean;
  autoAdvanceDelayMs: number;
  clickToAdvance: boolean;
}

export interface DialogueDefinition {
  id: string;
  config: DialogueConfig;
  lines: DialogueLine[];
}

// ---------------------------------------------------------------------------
// Dialogue loader – reads JSON files from src/data/dialogues/ via Vite glob
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
const dialogueModules = import.meta.glob('/src/data/dialogues/**/*.json', { eager: true });

const dialogueCache = new Map<string, DialogueDefinition>(
  Object.values(dialogueModules).map((raw) => {
    const def = ((raw as any).default ?? raw) as DialogueDefinition;
    return [def.id, def];
  }),
);

// Development only: the admin panel's test dialogues, with the characters and
// words only they use — Blake among them, whose likeness the game does not
// hold. A production build drops this branch and everything it reaches
// (check:dev-only), so there the tests open nothing.
export const testDialoguesLoaded: Promise<void> = import.meta.env.DEV
  ? Promise.all([import('../data/test-dialogues/test-dialogues'), import('../data/dialogue-characters')])
    .then(([test, characters]) => {
      for (const def of test.TEST_DIALOGUES) dialogueCache.set(def.id, def);
      characters.addCharacters(test.TEST_CHARACTERS);
      test.addTestDialogueWords();
    })
    .catch((err: unknown) => console.warn('[Dialogue] The test dialogues did not load:', err))
  : Promise.resolve();

export function loadDialogue(id: string): DialogueDefinition | undefined {
  return dialogueCache.get(id);
}

// ---------------------------------------------------------------------------
// City variables – a city reaches a line by its code and is named as it is drawn
// ---------------------------------------------------------------------------

/**
 * The dialogue variables the server fills with a city's airport code rather
 * than its name: `airport_hq`, the headquarters a team voted for
 * (phase-voting.go, DialogueCityHQ). A code is the same in every language and
 * a name is not, so a line names the city in its reader's language as it is
 * drawn — "Ciudad de México", "Cidade do México". phase_voting_test.go holds
 * these keys to the server's DialogueCity… constants.
 */
const CITY_VARIABLES = {
  airport_hq: true,
} as const;

/**
 * A line's values as its text reads them: each city variable's code given as
 * the city's name by `nameCity` — in a component
 * `useCityTranslation().getCityName`, which follows a change of language.
 * Every other value passes as it came: the debrief's check marks, ☑ or ☐
 * beside each of the session's missions.
 */
export function withCityNames(
  params: Record<string, string> | undefined,
  nameCity: (code: string) => string,
): Record<string, string> {
  const values = { ...params };
  for (const name of Object.keys(CITY_VARIABLES)) {
    const code = values[name];
    if (code) values[name] = nameCity(code);
  }
  return values;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface DialogueState {
  active: boolean;
  dialogueId: string | null;
  lines: DialogueLine[];
  currentIndex: number;
  config: DialogueConfig;
  revealComplete: boolean;

  startDialogueById: (id: string, variables?: Record<string, string>) => void;
  startDialogue: (definition: DialogueDefinition, variables?: Record<string, string>) => void;
  advanceDialogue: () => void;
  completeReveal: () => void;
  closeDialogue: () => void;
  reset: () => void;
}

/** What a dialogue that says nothing of its own does: what every session dialogue says — a line moves on with its narration, or its clock. */
const DEFAULT_CONFIG: DialogueConfig = {
  autoAdvance: false,
  autoAdvanceDelayMs: 4000,
  clickToAdvance: true,
};

const initialState = {
  active: false,
  dialogueId: null as string | null,
  lines: [] as DialogueLine[],
  currentIndex: 0,
  config: DEFAULT_CONFIG,
  revealComplete: false,
};

/**
 * Tell the server a dialogue is done with, so a session step waiting on it
 * can end (its custom_event end trigger, "dialogue_complete:<id>") and the
 * team's activity can count the player as having seen it.
 */
function sendDialogueComplete(dialogueId: string) {
  void sendToMatch(OpCode.OpCodeDialogueComplete, { type: MessageType.MessageTypeDialogueComplete, dialogue_id: dialogueId }, "a dialogue's end");
}

export const useDialogueStore = create<DialogueState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      startDialogueById: (id: string, variables?: Record<string, string>) => {
        const def = loadDialogue(id);
        if (!def) {
          console.warn(`[Dialogue] Definition not found: ${id}`);
          return;
        }
        get().startDialogue(def, variables);
      },

      startDialogue: (definition: DialogueDefinition, variables?: Record<string, string>) => {
        if (definition.lines.length === 0) return;
        // A dialogue still open when another arrives is done with: the server
        // hears so, as it would from a close, or a step waiting on it waits
        // for good. Only the server — the activity's own bookkeeping a close
        // does belongs to the phase now open, the new dialogue's. The same
        // dialogue again is not done with: it starts over.
        const open = get().dialogueId;
        if (open && open !== definition.id) sendDialogueComplete(open);
        const lines = variables
          ? definition.lines.map((line) => ({
              ...line,
              textParams: { ...(line.textParams ?? {}), ...variables },
            }))
          : definition.lines;
        set(
          {
            active: true,
            dialogueId: definition.id,
            lines,
            currentIndex: 0,
            config: { ...DEFAULT_CONFIG, ...definition.config },
            revealComplete: false,
          },
          false,
          'startDialogue',
        );
      },

      advanceDialogue: () => {
        const { currentIndex, lines } = get();
        const next = currentIndex + 1;
        if (next >= lines.length) {
          get().closeDialogue();
        } else {
          set({ currentIndex: next, revealComplete: false }, false, 'advanceDialogue');
        }
      },

      completeReveal: () => {
        set({ revealComplete: true }, false, 'completeReveal');
      },

      closeDialogue: () => {
        const closingId = get().dialogueId;
        set({ ...initialState }, false, 'closeDialogue');
        // Notify the server if a dialogue ID was set, so episode steps can react
        // via custom_event end triggers (e.g. "dialogue_complete:intro-session/intro").
        if (closingId) {
          // A phase waiting on the dialogue hears it is done with (team-activity.ts).
          handleDialogueClosed();
          sendDialogueComplete(closingId);
        }
      },

      reset: () => {
        set({ ...initialState }, false, 'reset');
      },
    }),
    { name: 'dialogue-store' },
  ),
);

// ---------------------------------------------------------------------------
// The player's acts — and the admin panel's
// ---------------------------------------------------------------------------

/**
 * A dialogue opens by its id, with the variables its lines read: the admin
 * panel's test, and as the other domains open one — a session step
 * (match-session-script.ts), a team activity's dialogue phase
 * (team-activity.ts), an award's celebration (achievements.ts).
 */
export function openDialogue(id: string, variables?: Record<string, string>): void {
  useDialogueStore.getState().startDialogueById(id, variables);
}

/** The line still typing shows whole: the player pressed on it, or its typing ended. */
export function showDialogueLineWhole(): void {
  useDialogueStore.getState().completeReveal();
}

/** On to the next line; past the last, the dialogue closes. */
export function advanceDialogue(): void {
  useDialogueStore.getState().advanceDialogue();
}

/**
 * The dialogue closes — its last line's Close, or its own recovery when it
 * cannot draw: the server hears it is done with, and a phase waiting on it
 * counts the player as having seen it.
 */
export function closeDialogue(): void {
  useDialogueStore.getState().closeDialogue();
}
